/**
 * wallet-appearance-api — the per-field feature-settings route that backs the
 * Apple Wallet pass appearance colours (JAI-581), through the
 * `@v0y4ge/api-client` `apiRaw` choke point (tax-api / payments-api pattern).
 *
 * WHY THIS IS NOT `updateIntegrationConfig` (2026-08-13, platform-confirmed):
 * `PATCH /v1/admin/integrations/{provider}/config` returns 400
 * CONFIG_NOT_SUPPORTED unless the provider is in a generic-provider allow-list
 * (sendgrid, twilio, webex, goto, gmeet, msteams). Apple is not in it, and
 * adding it would not help: that route writes a provider-config store with no
 * concept of features at all. The appearance fields live in
 * `integration_feature_settings`, keyed (company_id, provider, feature_key,
 * field_key), which has its own route — the one below.
 *
 * ONE PUT PER FIELD. There is no batch form. A three-colour save is three
 * requests, so the caller owns the partial-failure question: this module does
 * not pretend the set is atomic.
 *
 * Clearing is `{ value: null }` — an unconditional delete that returns 200 even
 * for a never-set field, so it is a no-op rather than an error path. Empty
 * string and null stay distinguishable at the backend on purpose ("blank this"
 * vs "inherit the default"), so never send "" to clear.
 *
 * Belongs in `@v0y4ge/api-client` alongside `putIntegrationFeatureCredential`
 * once the kit ships it; it is local only because api-client is kit-owned.
 */
import { apiRaw, normalizeIntegrationProvider } from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";

const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });

export interface FeatureSettingResult {
  fieldKey: string;
  /** The stored value, or null when the field is unset / was just cleared. */
  value: string | null;
  /** null on a clear (there is no stored row to carry a timestamp). */
  updatedAt: string | null;
}

/**
 * Write or clear one feature setting.
 *
 * cap: integrations.write · scope: org.
 * `value: null` clears the field and returns 200 whether or not it was ever set.
 */
export const putIntegrationFeatureSetting = (
  provider: string,
  featureKey: string,
  fieldKey: string,
  value: string | null,
) => {
  const p = normalizeIntegrationProvider(provider);
  return tag("putIntegrationFeatureSetting", apiRaw<FeatureSettingResult | { data: FeatureSettingResult }>(
    `/v1/admin/integrations/${encodeURIComponent(p)}/features/${encodeURIComponent(featureKey)}/settings/${encodeURIComponent(fieldKey)}`,
    { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify({ value }) },
  ));
};
